import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ProgressImagePanel.css';

export default function ProgressImagePanel({ backendService, userId }) {
 // Remount synchronously on account/target changes: never paint another scope's pixels.
 return <ScopedProgressImagePanel key={`${backendService.authenticatedIdentity || ''}:${userId}`} backendService={backendService} userId={userId} />;
}

function ScopedProgressImagePanel({ backendService, userId }) {
 const [metadata, setMetadata] = useState(null);
 const [src, setSrc] = useState(null);
 const [file, setFile] = useState(null);
 const [pending, setPending] = useState('');
 const [error, setError] = useState('');
 const [status, setStatus] = useState('');
 const current = useRef(null);
 const objectUrl = useRef(null);
 const input = useRef(null);
 const disposeImage = useCallback(() => {
  if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
  objectUrl.current = null;
  setSrc(null);
 }, []);
 const run = useCallback(async (action = 'load', image) => {
  if (current.current || !userId) return;
  const request = { controller: new AbortController(), identity: backendService.authenticatedIdentity };
  current.current = request;
  const valid = () => current.current === request && !request.controller.signal.aborted && request.identity === backendService.authenticatedIdentity;
  const options = { signal: request.controller.signal };
  setPending(action); setError(''); setStatus('');
  try {
   if (action === 'upload') await backendService.uploadProgressImage(userId, image, options);
   if (action === 'remove') await backendService.removeProgressImage(userId, options);
   if (!valid()) return;
   // Always re-read authority and the canonical descriptor after mutation.
   const data = await backendService.getProgressImage(userId, options);
   if (!valid()) return;
   setMetadata(data); disposeImage();
   if (data.progress_image?.image_url) {
    const blob = await backendService.fetchProgressImage(userId, data.progress_image.image_url, options);
    if (!valid()) return;
    objectUrl.current = URL.createObjectURL(blob); setSrc(objectUrl.current);
   }
   if (!valid()) return;
   if (action !== 'load') {
    setFile(null); if (input.current) input.current.value = '';
    setStatus(action === 'remove' ? 'Progress image removed.' : 'Progress image saved.');
   }
  } catch (_) {
   if (valid()) {
    disposeImage(); setMetadata(null);
    setError(action === 'load' ? 'Could not load progress image. Retry to refresh permissions.' : 'Could not confirm the progress image change. Retry to refresh before editing again.');
   }
  } finally {
   if (current.current === request) { current.current = null; setPending(''); }
  }
 }, [backendService, userId, disposeImage]);
 useEffect(() => {
  setMetadata(null); setFile(null); disposeImage(); run();
  return () => {
   current.current?.controller.abort(); current.current = null;
   if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
   objectUrl.current = null;
  };
 }, [run, disposeImage]);
 const cancel = () => {
  current.current?.controller.abort(); current.current = null;
  setPending(''); setMetadata(null); setFile(null); disposeImage();
  setStatus('Request cancelled. A change may already have reached the server; refresh before editing again.');
 };
 const choose = event => {
  const selected = event.target.files?.[0]; setError('');
  if (!selected) { setFile(null); return; }
  if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(selected.type) || selected.size > 12 * 1024 * 1024) {
   setFile(null); event.target.value = ''; setError('Choose a JPEG, PNG, WebP or GIF image up to 12 MB.'); return;
  }
  setFile(selected);
 };
 return <section className="progress-image-panel" aria-label="Personal progress image" aria-busy={Boolean(pending)}>
  <h3>Personal progress image</h3>
  <p>A separate progress picture, not your profile avatar. Image changes save immediately.</p>
  {src ? <img className="progress-image-photo" src={src} alt="Personal progress" /> : !pending && metadata && <p>{metadata.progress_image ? 'Progress image preview unavailable.' : backendService.authenticatedIdentity === String(userId) ? 'No progress image saved.' : 'No progress image shared with you.'}</p>}
  {metadata?.managed_by_dojo && <p className="progress-image-managed">Managed by your dojo chief. Sharing remains independent and editable in Privacy.</p>}
  {metadata?.can_edit && <div className="progress-image-controls">
   <label>Choose progress image<input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={choose} disabled={Boolean(pending)} /></label>
   {file && <span>{file.name}</span>}
   <button type="button" onClick={() => run('upload', file)} disabled={!file || Boolean(pending)}>{pending === 'upload' ? 'Uploading…' : 'Upload progress image'}</button>
   <button type="button" onClick={() => run('remove')} disabled={Boolean(pending)}>{pending === 'remove' ? 'Removing…' : 'Remove progress image'}</button>
  </div>}
  {pending && <div role="status">{pending === 'load' ? 'Loading progress image…' : pending === 'upload' ? 'Uploading progress image…' : 'Removing progress image…'} <button type="button" onClick={cancel}>Cancel progress image request</button></div>}
  {error && <p role="alert">{error}</p>}
  {status && <p role="status">{status}</p>}
  {!pending && <button type="button" onClick={() => run()}>{error || !metadata ? 'Retry progress image' : 'Refresh progress image'}</button>}
 </section>;
}
